import { awaitJSON, fetchCometSelfInvoke, MetricsLambdaFetch } from '../../../../utils/fetch-utils';
import { buildRunChannelColors } from './dtc-commerce-mmm-channel-colors';
import { initIconMapping, registerChannelPlatforms } from './dtc-commerce-mmm-icon-resolver';
import { aggregateTwoStageDecomp } from './dtc-commerce-mmm-two-stage';
import {
  ActualVsPredictedRow,
  AttributedData,
  CPTBRow,
  ChannelHierarchy,
  ChannelInfo,
  DashboardData,
  DateRange,
  DateRangePreset,
  DateRangePresetConst,
  DecayRow,
  ModelInfo,
  ROASRow,
  SaturationRow,
  TwoStageDecompRow,
  ValidationResult
} from '../dtc-commerce-mmm-types';

export type CsvRow = Record<string, string>;

export const parseNum = (val: string | undefined | null): number => {
  if (val === undefined || val === null || val === '') return 0;
  const trimmed = val.trim();
  if (trimmed === '' || trimmed.toLowerCase() === 'nan') return 0;
  const n = Number(trimmed);
  if (Number.isNaN(n)) return 0;
  return Object.is(n, -0) ? 0 : n;
};

export const parseNumOrNull = (val: string | undefined | null): number | null => {
  if (val === undefined || val === null || val === '') return null;
  const trimmed = val.trim();
  if (trimmed === '' || trimmed.toLowerCase() === 'nan') return null;
  const n = Number(trimmed);
  if (Number.isNaN(n)) return null;
  return Object.is(n, -0) ? 0 : n;
};

export const parseBool = (val: string | undefined | null): boolean => {
  if (!val) return false;
  const t = val.trim().toUpperCase();
  return t === 'TRUE' || t === '1';
};

export interface LoadContext {
  client: string;
  branch: string;
  kpi: string;
  timestamp: string;
  outputFileVersion: string;
}

// Each refreshed-page data file is served by a metrics lambda that returns the same
// raw-meridian rows via isLegacyUI=false — for both Meridian (v2) and legacy (v1) runs.
// Each per-panel loader below calls its endpoint directly (same pattern as the old MMM.tsx page),
// and consumes the raw-meridian row shape.
const rowCache = new Map<string, Promise<CsvRow[]>>();

const fetchRows = async (ctx: LoadContext, endpoint: string): Promise<CsvRow[]> => {
  const res = await MetricsLambdaFetch(endpoint, {
    params: {
      company: ctx.client,
      branch: ctx.branch,
      kpi: ctx.kpi,
      date: ctx.timestamp,
      outputFileVersion: ctx.outputFileVersion,
      isLegacyUI: 'false'
    }
  });
  return awaitJSON<CsvRow[]>(res);
};

export const loadRows = (ctx: LoadContext, endpoint: string): Promise<CsvRow[]> => {
  const cacheKey = `${ctx.branch}|${ctx.client}|${ctx.kpi}|${ctx.timestamp}|${endpoint}`;
  const cached = rowCache.get(cacheKey);
  if (cached) return cached;
  const promise = fetchRows(ctx, endpoint).catch(err => {
    rowCache.delete(cacheKey);
    throw err;
  });
  rowCache.set(cacheKey, promise);
  return promise;
};

const isNationalRow = (r: CsvRow): boolean => !r.geo || r.geo === 'national_geo';

const applyGlobalFilters = (rows: CsvRow[]): CsvRow[] =>
  rows.filter(r => isNationalRow(r) && (!r.estimate_type || r.estimate_type === 'mean'));

export const parseModelInfo = (rows: CsvRow[]): ModelInfo => {
  const map: Record<string, string> = {};
  rows.forEach(r => {
    map[r.parameter] = r.value;
  });

  const rawTestMape = parseNum(map.test_mape);
  const testMape = rawTestMape < 1 ? rawTestMape * 100 : rawTestMape;

  const kpiType: 'revenue' | 'non-revenue' =
    map.kpi_type === 'non-revenue' || map.kpi_type === 'non_revenue' ? 'non-revenue' : 'revenue';

  return {
    client: map.client_name || map.client || '',
    kpi: map.model_kpi || map.kpi || '',
    kpiType,
    dateGranularity: map.date_granularity || 'weekly',
    refreshDate: map.refresh_date || (map.refresh_timestamp || '').slice(0, 10) || '',
    trainStartDate: map.train_start_date || '',
    trainEndDate: map.train_end_date || '',
    testStartDate: map.test_start_date || '',
    testEndDate: map.test_end_date || '',
    trainSize: parseNum(map.train_size),
    testSize: parseNum(map.test_size),
    trainRsquared: parseNum(map.train_rsquared),
    testRsquared: parseNum(map.test_rsquared),
    testMape,
    testMae: parseNum(map.test_mae),
    // A geo model's n_observations counts every DMA-week; n_weeks is its time-series length.
    // National files carry only n_observations, which there is already the week count.
    nObservations: parseNumOrNull(map.n_weeks) ?? parseNum(map.n_observations),
    meanSpend: parseNum(map.mean_spend),
    medianSpend: parseNum(map.median_spend),
    confidenceLevel: parseNum(map.confidence_level)
  };
};

const loadModelInfoFile = async (ctx: LoadContext): Promise<ModelInfo> =>
  parseModelInfo(await loadRows(ctx, '/getModelOverviewData'));

const mapCPTBRow = (r: CsvRow): CPTBRow => ({
  date: r.date || '',
  name: r.name || '',
  channel: r.channel || '',
  platform: r.platform || '',
  tactic: r.tactic || '',
  brandNonbrand: r.brand_nonbrand || '',
  incrementalityTest: parseBool(r.incrementality_test),
  mediaType: r.media_type || '',
  rawSpend: parseNum(r.spend),
  effectiveSpend: parseNum(r.spend),
  effectiveNormalized: parseNum(r.contribution_normalized_to_observed),
  ciLo: 0,
  ciHi: 0
});

const loadCPTBFile = async (
  ctx: LoadContext
): Promise<{ actualized: CPTBRow[]; raw: CPTBRow[] }> => {
  const rows = await loadRows(ctx, '/getSpendEffectOverTimeData');
  const nationalRows = rows.filter(r => r.geo === 'national_geo');

  const ciLookup = new Map<string, { ciLo: number; ciHi: number }>();
  nationalRows.forEach(r => {
    if (r.estimate_type !== 'ci_lo' && r.estimate_type !== 'ci_hi') return;
    const key = `${r.name}|${r.date}|${r.attribution_type}`;
    const entry = ciLookup.get(key) ?? { ciLo: 0, ciHi: 0 };
    if (r.estimate_type === 'ci_lo') {
      entry.ciLo = parseNum(r.contribution_normalized_to_observed);
    } else {
      entry.ciHi = parseNum(r.contribution_normalized_to_observed);
    }
    ciLookup.set(key, entry);
  });

  const meanRows = nationalRows.filter(r => r.estimate_type === 'mean');

  const mapWithCI = (r: CsvRow): CPTBRow => {
    const row = mapCPTBRow(r);
    const key = `${r.name}|${r.date}|${r.attribution_type}`;
    const ci = ciLookup.get(key);
    if (ci) {
      row.ciLo = ci.ciLo;
      row.ciHi = ci.ciHi;
    }
    return row;
  };

  return {
    actualized: meanRows.filter(r => r.attribution_type === 'actualized').map(mapWithCI),
    raw: meanRows.filter(r => r.attribution_type === 'raw').map(mapWithCI)
  };
};

// A geo model writes one row per DMA per date — plus `other`, the DMAs outside the model — and
// no national row, so its national series is the sum across every geo. That sum is what econ's
// national fit metrics are computed on. Its holdout is drawn per DMA-week, so a summed week mixes
// train and test DMAs; the week takes whichever covers most of them (a tie goes to train). The
// label only feeds the fit chart's hover and the AvP export's Window column.
//
// Every week must carry the same geos: a geo missing from some weeks would quietly dip those
// weeks' totals, so that throws rather than rendering a series that looks plausible.
const sumAcrossGeos = (rows: CsvRow[]): ActualVsPredictedRow[] => {
  const byDate = new Map<string, ActualVsPredictedRow & { geos: number; testGeos: number }>();
  rows.forEach(r => {
    if (r.estimate_type && r.estimate_type !== 'mean') return;
    const date = r.date || '';
    const entry = byDate.get(date) ?? {
      date,
      actualKpi: 0,
      expectedKpi: 0,
      baselinePrediction: 0,
      trainTest: '',
      geos: 0,
      testGeos: 0
    };
    entry.actualKpi += parseNum(r.actual_kpi);
    entry.expectedKpi += parseNum(r.expected_kpi);
    entry.baselinePrediction += parseNum(r.baseline_prediction);
    entry.geos += 1;
    if (r.train_test === 'test') {
      entry.testGeos += 1;
    }
    byDate.set(date, entry);
  });
  const weeks = Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  const geoCount = Math.max(0, ...weeks.map(w => w.geos));
  const shortWeek = weeks.find(w => w.geos !== geoCount);
  if (shortWeek) {
    throw new Error(
      `Actual vs. predicted is missing geos: ${shortWeek.date} has ${shortWeek.geos} of ${geoCount}.`
    );
  }
  return weeks.map(({ geos, testGeos, ...row }) => ({
    ...row,
    trainTest: testGeos * 2 > geos ? 'test' : 'train'
  }));
};

// A file is per-geo when it names at least one geo and has no national_geo row. Deciding on the
// whole file keeps a stray blank geo cell from tipping a geo file onto the national path.
const isPerGeoFile = (rows: CsvRow[]): boolean =>
  rows.some(r => r.geo && r.geo !== 'national_geo') && !rows.some(r => r.geo === 'national_geo');

export const parseActualVsPredicted = (rows: CsvRow[]): ActualVsPredictedRow[] => {
  if (isPerGeoFile(rows)) return sumAcrossGeos(rows);
  return applyGlobalFilters(rows).map(r => ({
    date: r.date || '',
    actualKpi: parseNum(r.actual_kpi),
    expectedKpi: parseNum(r.expected_kpi),
    baselinePrediction: parseNum(r.baseline_prediction),
    trainTest: r.train_test || ''
  }));
};

const loadActualVsPredictedFile = async (ctx: LoadContext): Promise<ActualVsPredictedRow[]> =>
  parseActualVsPredicted(await loadRows(ctx, '/getActualVsPredictedData'));

const mapROASRow = (r: CsvRow): ROASRow => {
  const pqRoas = parseNumOrNull(r.previous_qtr_roas);
  const atRoas = parseNumOrNull(r.all_time_roas);
  const pqCpa = parseNumOrNull(r.previous_qtr_cpa);
  const atCpa = parseNumOrNull(r.all_time_cpa);
  const nde = parseNum(r.next_dollar_effectiveness);
  const pqSpend = parseNum(r.previous_qtr_average_spend);
  const respPqSpend = parseNum(r.response_to_previous_qtr_average_spend);

  return {
    name: r.name || '',
    channel: r.channel || '',
    platform: r.platform || '',
    tactic: r.tactic || '',
    brandNonbrand: r.brand_nonbrand || '',
    incrementalityTest: parseBool(r.incrementality_test),
    mediaType: r.media_type || '',
    darkLastQuarter: parseBool(r.dark_last_quarter),
    previousQtrRoas: pqRoas,
    effectivePreviousQtrRoas: pqRoas,
    previousQtrCpa: pqCpa,
    effectivePreviousQtrCpa: pqCpa,
    allTimeRoas: atRoas,
    effectiveAllTimeRoas: atRoas,
    allTimeCpa: atCpa,
    effectiveAllTimeCpa: atCpa,
    previousQtrMeanSpend: pqSpend,
    effectivePreviousQtrMeanSpend: pqSpend,
    responsePreviousQtrMeanSpend: respPqSpend,
    effectiveResponsePreviousQtrMeanSpend: respPqSpend,
    nextDollarEffectiveness: nde,
    effectiveNextDollarEffectiveness: nde,
    spendNeededForOneMoreKpi: parseNumOrNull(r.spend_needed_for_one_more_kpi)
  };
};

const loadROASFile = async (
  ctx: LoadContext
): Promise<{ actualized: ROASRow[]; raw: ROASRow[] }> => {
  const rows = await loadRows(ctx, '/getMarginalDecisionData');
  const base = rows.filter(r => r.geo === 'national_geo' && r.estimate_type === 'mean');
  return {
    actualized: base.filter(r => r.attribution_type === 'actualized').map(mapROASRow),
    raw: base.filter(r => r.attribution_type === 'raw').map(mapROASRow)
  };
};

const mapSaturationRow = (r: CsvRow): SaturationRow => {
  const spend = parseNum(r.spend);
  const resp = parseNum(r.response);
  const rnd = parseNum(r.return_next_dollar);

  return {
    name: r.name || '',
    channel: r.channel || '',
    platform: r.platform || '',
    tactic: r.tactic || '',
    brandNonbrand: r.brand_nonbrand || '',
    mediaType: r.media_type || '',
    adstockSpend: spend,
    effectiveAdstockSpend: spend,
    response: resp,
    effectiveResponse: resp,
    returnNextDollar: rnd,
    effectiveReturnNextDollar: rnd,
    isMean: r.previous_qtr_average_value === '1',
    ciLoResponse: 0,
    ciHiResponse: 0,
    ciLoReturnNextDollar: 0,
    ciHiReturnNextDollar: 0
  };
};

const loadSaturationFile = async (
  ctx: LoadContext
): Promise<{
  actualized: SaturationRow[];
  raw: SaturationRow[];
}> => {
  const rows = await loadRows(ctx, '/getResponseCurvesData');
  const nationalRows = rows.filter(r => r.geo === 'national_geo');

  const ciLookup = new Map<
    string,
    { lo: number; hi: number; loNextDollar: number; hiNextDollar: number }
  >();
  nationalRows.forEach(r => {
    if (r.estimate_type !== 'ci_lo' && r.estimate_type !== 'ci_hi') return;
    const key = `${r.name}|${r.spend}|${r.attribution_type}`;
    const entry = ciLookup.get(key) ?? { lo: 0, hi: 0, loNextDollar: 0, hiNextDollar: 0 };
    if (r.estimate_type === 'ci_lo') {
      entry.lo = parseNum(r.response);
      entry.loNextDollar = parseNum(r.return_next_dollar);
    } else {
      entry.hi = parseNum(r.response);
      entry.hiNextDollar = parseNum(r.return_next_dollar);
    }
    ciLookup.set(key, entry);
  });

  const meanRows = nationalRows.filter(r => r.estimate_type === 'mean');

  const mapWithCI = (r: CsvRow): SaturationRow => {
    const row = mapSaturationRow(r);
    const key = `${r.name}|${r.spend}|${r.attribution_type}`;
    const ci = ciLookup.get(key);
    if (ci) {
      row.ciLoResponse = ci.lo;
      row.ciHiResponse = ci.hi;
      row.ciLoReturnNextDollar = ci.loNextDollar;
      row.ciHiReturnNextDollar = ci.hiNextDollar;
    }
    return row;
  };

  return {
    actualized: meanRows.filter(r => r.attribution_type === 'actualized').map(mapWithCI),
    raw: meanRows.filter(r => r.attribution_type === 'raw').map(mapWithCI)
  };
};

const loadDecayFile = async (ctx: LoadContext): Promise<DecayRow[]> => {
  const rows = await loadRows(ctx, '/getDecayCurvesData');
  const nationalRows = rows.filter(r => !r.geo || r.geo === 'national_geo');
  const resolveName = (r: CsvRow): string => r.clean_name || r.name || '';

  const ciLookup = new Map<string, { ciLoVal: number; ciHiVal: number }>();
  nationalRows.forEach(r => {
    if (r.estimate_type !== 'ci_lo' && r.estimate_type !== 'ci_hi') return;
    const key = `${resolveName(r)}|${r.period}`;
    const entry = ciLookup.get(key) ?? { ciLoVal: 0, ciHiVal: 0 };
    if (r.estimate_type === 'ci_lo') {
      entry.ciLoVal = parseNum(r.spend_carryover);
    } else {
      entry.ciHiVal = parseNum(r.spend_carryover);
    }
    ciLookup.set(key, entry);
  });

  const meanRows = nationalRows.filter(r => r.estimate_type === 'mean');

  return meanRows.map(r => {
    const name = resolveName(r);
    const key = `${name}|${r.period}`;
    const ci = ciLookup.get(key);
    return {
      period: parseNum(r.period),
      name,
      spendCarryover: parseNum(r.spend_carryover),
      maxLag: parseNum(r.max_lag),
      ciLoCarryover: ci ? Math.min(ci.ciLoVal, ci.ciHiVal) : 0,
      ciHiCarryover: ci ? Math.max(ci.ciLoVal, ci.ciHiVal) : 0
    };
  });
};

const loadTwoStageDecompFile = async (ctx: LoadContext): Promise<TwoStageDecompRow[]> => {
  // Two-stage is large (megabytes) and trips the lambda response-size guard over plain HTTP, so
  // fetch it through the comet self-invoke flow (S3-backed) — same as the old MMM page's getTwoStageData call.
  const rows =
    (await fetchCometSelfInvoke<Record<string, string>, CsvRow[]>(
      MetricsLambdaFetch,
      '/getTwoStageData',
      {
        company: ctx.client,
        branch: ctx.branch,
        kpi: ctx.kpi,
        date: ctx.timestamp,
        outputFileVersion: ctx.outputFileVersion,
        isLegacyUI: 'false'
      },
      { method: 'GET' }
    )) ?? [];
  const filtered = rows.filter(
    r =>
      r.estimate_type === 'mean' &&
      r.geo === 'national_geo' &&
      (r.category_type === 'added' || r.category_type === 'reduced')
  );

  return filtered.map(r => {
    const categoryType: 'added' | 'reduced' = r.category_type === 'added' ? 'added' : 'reduced';
    const spendOrEffect: 'spend' | 'effect' = r.spend_or_effect === 'spend' ? 'spend' : 'effect';
    return {
      date: r.date || '',
      primaryName: r.primary_name || '',
      primaryMediaType: r.primary_media_type || '',
      secondaryName: r.secondary_name || '',
      secondaryMediaType: r.secondary_media_type || '',
      categoryType,
      spendOrEffect,
      value: parseNum(r.value)
    };
  });
};

const loadChannelLookup = async (ctx: LoadContext): Promise<ChannelInfo[]> => {
  const rows = await loadRows(ctx, '/getFeatureMetadata');
  return rows.map(r => ({
    name: r.name || '',
    channel: r.channel || '',
    platform: r.platform || '',
    tactic: r.tactic || '',
    brandNonbrand: r.brand_nonbrand || '',
    mediaType: r.media_type || '',
    incrementalityTest: parseBool(r.incrementality_test),
    isDark: false
  }));
};

const buildChannelHierarchy = (lookupChannels: ChannelInfo[]): ChannelHierarchy => {
  const channelMap = new Map<string, ChannelInfo>();
  lookupChannels.forEach(ch => {
    channelMap.set(ch.name, { ...ch });
  });

  const channels = Array.from(channelMap.values());

  return {
    channels,
    uniqueChannels: Array.from(new Set(channels.map(c => c.channel))).sort(),
    uniquePlatforms: Array.from(new Set(channels.map(c => c.platform).filter(Boolean))).sort(),
    uniqueTactics: Array.from(new Set(channels.map(c => c.tactic).filter(Boolean))).sort(),
    uniqueBrandNonbrand: Array.from(
      new Set(channels.map(c => c.brandNonbrand).filter(Boolean))
    ).sort(),
    uniqueMediaTypes: Array.from(new Set(channels.map(c => c.mediaType).filter(Boolean))).sort()
  };
};

export const filterDateWindow = <T extends { date: string }>(
  rows: T[],
  startDate: string,
  endDate: string
): T[] => {
  const start = new Date(`${startDate}T00:00:00`).getTime();
  const end = new Date(`${endDate}T00:00:00`).getTime();
  return rows.filter(r => {
    const t = new Date(`${r.date}T00:00:00`).getTime();
    return t >= start && t <= end;
  });
};

export const computeDateRangeFromPreset = (
  preset: DateRangePreset,
  dataStartDate: string,
  dataEndDate: string
): DateRange => {
  const end = new Date(`${dataEndDate}T00:00:00`);
  const dataStart = new Date(`${dataStartDate}T00:00:00`);
  let start: Date;
  let label: string;

  switch (preset) {
    case DateRangePresetConst.T3M:
      start = new Date(end);
      start.setDate(start.getDate() - 13 * 7);
      label = 'Trailing 3 months';
      break;
    case DateRangePresetConst.T6M:
      start = new Date(end);
      start.setDate(start.getDate() - 26 * 7);
      label = 'Trailing 6 months';
      break;
    case DateRangePresetConst.T12M:
      start = new Date(end);
      start.setDate(start.getDate() - 52 * 7);
      label = 'Trailing 12 months';
      break;
    case DateRangePresetConst.YTD:
      start = new Date(end.getFullYear(), 0, 1);
      label = `Year to date (${end.getFullYear()})`;
      break;
    case DateRangePresetConst.ALL:
      start = dataStart;
      label = 'All time';
      break;
    case DateRangePresetConst.CUSTOM:
    default:
      return {
        preset: DateRangePresetConst.CUSTOM,
        startDate: dataStartDate,
        endDate: dataEndDate,
        label: 'Custom range'
      };
  }

  if (start < dataStart) {
    start = dataStart;
  }

  return {
    preset,
    startDate: localDateKey(start),
    endDate: dataEndDate,
    label
  };
};

// Formats a local Date as YYYY-MM-DD without UTC conversion.
// Never use toISOString().slice(0,10) for local dates — it converts to UTC
// first and rolls back a day for any timezone behind UTC.
const localDateKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const calculateT12MWindow = <T extends { date: string }>(
  rows: T[]
): { filtered: T[]; startDate: string; endDate: string } => {
  if (rows.length === 0) return { filtered: [], startDate: '', endDate: '' };

  // Parse as local midnight (T00:00:00) so date arithmetic matches filterDateWindow
  // and computeDateRangeFromPreset, both of which also use local midnight.
  const dates = rows.map(r => new Date(`${r.date}T00:00:00`).getTime());
  const maxTime = dates.reduce((m, t) => (t > m ? t : m), -Infinity);
  const endDate = new Date(maxTime);
  const startDate = new Date(maxTime);
  startDate.setDate(startDate.getDate() - 52 * 7);
  const startTime = startDate.getTime();

  const filtered = rows.filter(r => new Date(`${r.date}T00:00:00`).getTime() >= startTime);

  return {
    filtered,
    startDate: localDateKey(startDate),
    endDate: localDateKey(endDate)
  };
};

const computeValidation = (
  baselineRows: ActualVsPredictedRow[],
  cptbRows: CPTBRow[]
): ValidationResult => {
  const totalBaseline = baselineRows.reduce((s, r) => s + r.baselinePrediction, 0);
  const totalChannelContrib = cptbRows.reduce((s, r) => s + r.effectiveNormalized, 0);
  const totalActualKpi = baselineRows.reduce((s, r) => s + r.actualKpi, 0);
  const combined = totalBaseline + totalChannelContrib;
  const pctOfActual = totalActualKpi > 0 ? (combined / totalActualKpi) * 100 : 0;
  const passed = totalActualKpi > 0 && Math.abs(combined - totalActualKpi) / totalActualKpi < 0.05;
  return { totalBaseline, totalChannelContrib, totalActualKpi, pctOfActual, passed };
};

export const loadDtcCommerceMmmData = async (ctx: LoadContext): Promise<DashboardData> => {
  const [modelInfo, cptbBoth, actualVsPredicted, roasBoth, satBoth, decay, lookupChannels] =
    await Promise.all([
      loadModelInfoFile(ctx),
      loadCPTBFile(ctx),
      loadActualVsPredictedFile(ctx),
      loadROASFile(ctx),
      loadSaturationFile(ctx),
      loadDecayFile(ctx),
      loadChannelLookup(ctx),
      initIconMapping()
    ]);
  const twoStageDecompRows = await loadTwoStageDecompFile(ctx);

  // Meridian writes a synthetic "Covariates" row into spend_effect_over_time that carries
  // the non-paid-media baseline (often the entire KPI). It is not a paid channel and must
  // not feed any paid-media calculation. The channel lookup is the authoritative list of
  // paid-media features — drop any CPTB row whose name is not in it.
  const paidMediaNames = new Set(lookupChannels.map(c => c.name));
  cptbBoth.actualized = cptbBoth.actualized.filter(r => paidMediaNames.has(r.name));
  cptbBoth.raw = cptbBoth.raw.filter(r => paidMediaNames.has(r.name));

  const cptbDates = cptbBoth.actualized.map(r => new Date(`${r.date}T00:00:00`).getTime());
  const maxCptbTime =
    cptbDates.length > 0 ? cptbDates.reduce((m, t) => (t > m ? t : m), -Infinity) : Date.now();
  const minCptbTime =
    cptbDates.length > 0 ? cptbDates.reduce((m, t) => (t < m ? t : m), Infinity) : Date.now();
  const dataEndDate = localDateKey(new Date(maxCptbTime));
  const dataStartDate = localDateKey(new Date(minCptbTime));

  const {
    filtered: actCptbT12M,
    startDate: t12mStartDate,
    endDate: t12mEndDate
  } = calculateT12MWindow(cptbBoth.actualized);
  const { filtered: rawCptbT12M } = calculateT12MWindow(cptbBoth.raw);
  const { filtered: actualVsPredictedT12M } = calculateT12MWindow(actualVsPredicted);

  const twoStageDecomp = aggregateTwoStageDecomp(
    twoStageDecompRows,
    actualVsPredicted,
    t12mStartDate,
    t12mEndDate
  );
  const channelHierarchy = buildChannelHierarchy(lookupChannels);
  registerChannelPlatforms(channelHierarchy.channels);
  // Whole-run spend, so colors stay put when the date range changes. Lookup channels with no
  // spend rows still get a color.
  const channelColors = buildRunChannelColors([
    ...channelHierarchy.uniqueChannels.map(channel => ({ channel, spend: 0 })),
    ...cptbBoth.actualized.map(r => ({ channel: r.channel, spend: r.rawSpend }))
  ]);

  const actualized: AttributedData = {
    cptb: cptbBoth.actualized,
    cptbT12M: actCptbT12M,
    roas: roasBoth.actualized,
    saturation: satBoth.actualized
  };
  const raw: AttributedData = {
    cptb: cptbBoth.raw,
    cptbT12M: rawCptbT12M,
    roas: roasBoth.raw,
    saturation: satBoth.raw
  };

  return {
    modelInfo,
    actualized,
    raw,
    actualVsPredicted,
    actualVsPredictedT12M,
    decay,
    channelHierarchy,
    channelColors,
    twoStageDecomp,
    twoStageDecompRows,
    t12mStartDate,
    t12mEndDate,
    dataStartDate,
    dataEndDate,
    validation: computeValidation(actualVsPredictedT12M, actCptbT12M)
  };
};
